import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { VarietyRecord } from './types';

type SortKey = 'rowIndex' | 'variety' | 'lat' | 'lon';
type SortDir = 'asc' | 'desc';

interface DataTableProps {
  records: VarietyRecord[];
  selectedVariety: string;
}

export function DataTable({ records, selectedVariety }: DataTableProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('rowIndex');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sorted = useMemo(() => {
    const copy = [...records];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc'
        ? (av as number) - (bv as number)
        : (bv as number) - (av as number);
    });
    return copy;
  }, [records, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const arrow = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const isAll = selectedVariety === 'all';

  return (
    <div className="overflow-auto max-h-[400px]">
      <table className="w-full text-sm text-left">
        <thead className="sticky top-0 bg-white border-b border-gray-200">
          <tr>
            <th
              className="px-3 py-2 cursor-pointer hover:text-spi-green select-none"
              onClick={() => handleSort('rowIndex')}
            >
              {t('varietyMapper.tableNumber')}{arrow('rowIndex')}
            </th>
            <th
              className="px-3 py-2 cursor-pointer hover:text-spi-green select-none"
              onClick={() => handleSort('variety')}
            >
              {t('varietyMapper.tableVariety')}{arrow('variety')}
            </th>
            <th
              className="px-3 py-2 cursor-pointer hover:text-spi-green select-none"
              onClick={() => handleSort('lat')}
            >
              {t('varietyMapper.tableLat')}{arrow('lat')}
            </th>
            <th
              className="px-3 py-2 cursor-pointer hover:text-spi-green select-none"
              onClick={() => handleSort('lon')}
            >
              {t('varietyMapper.tableLon')}{arrow('lon')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const highlighted = !isAll && r.variety === selectedVariety;
            return (
              <tr
                key={r.rowIndex}
                className={highlighted ? 'bg-indigo-50' : 'hover:bg-gray-50'}
              >
                <td className="px-3 py-1.5 text-gray-500">{r.rowIndex}</td>
                <td className="px-3 py-1.5 font-medium">{r.variety}</td>
                <td className="px-3 py-1.5 tabular-nums">{r.lat.toFixed(5)}</td>
                <td className="px-3 py-1.5 tabular-nums">{r.lon.toFixed(5)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
